import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpApi from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { SUITE_LANGUAGES, matchLanguage, readPrefs } from "@zehnmindai/embed/react";
import { UI_LANGUAGE_CODES } from "./shared/utils/languages.js";
import { buildInfo } from "./shared/buildInfo";

// The languages docs ships, in the order the install's top bar lists them
// (the person menu, the same order in every module).
export const LANGUAGES = SUITE_LANGUAGES.filter((code) => UI_LANGUAGE_CODES.includes(code));

// The person's language across the install (the zehn_prefs cookie every
// module shares). Without one yet, the detector decides: what this browser
// chose in docs before the cookie ("language" in localStorage, read for one
// more release), else the browser's. PrefsProvider (main.jsx) keeps i18next
// in step after this.
const startLanguage = () => matchLanguage(readPrefs().language, UI_LANGUAGE_CODES) ?? undefined;

i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: startLanguage(),
    supportedLngs: UI_LANGUAGE_CODES,
    // A key missing from the generated Cyrillic locale shows Latin Uzbek,
    // never English (scripts/gen-uz-cyrl.mjs keeps the gap at zero).
    fallbackLng: { "uz-Cyrl": ["uz", "en"], default: ["en"] },
    debug: false,
    ns: [
      "common",
      "navigation",
      "my-docs",
      "mainPage",
      "signing",
      "shared",
      "sidebar",
      "apiKey",
      "shareModal",
      "version",
      "table",
      "pagination",
      "editor",
      "errors",
      "assistant",
      "console",
      "records",
      "search",
    ],
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // The catalogs are plain files, so a browser that cached one keeps it
      // after a deploy and shows raw keys for every string added since. The
      // build changes with each image, so a build never reuses an old copy.
      queryStringParams: { v: buildInfo.build },
    },
    detection: {
      // No htmlTag: index.html hardcodes lang="en", which used to pin every
      // fresh session to English before navigator was ever consulted.
      order: ["localStorage", "navigator"],
      // The cookie is the store now (PrefsProvider), so nothing is cached here.
      caches: [],
      lookupLocalStorage: "language",
    },
    react: {
      useSuspense: true,
      bindI18n: 'languageChanged',
    },
  });

// Keep <html lang> honest (a11y, hyphenation, extension heuristics).
i18n.on("languageChanged", (lng) => {
  try {
    document.documentElement.lang = lng;
  } catch { /* non-DOM env */ }
});

/** PrefsProvider's onLanguage: i18next follows the person's language (a no-op when it already shows it). */
export const showLanguage = (language) => {
  if (i18n.language !== language) void i18n.changeLanguage(language);
};

export default i18n;
